import { useState } from "react";
import { MdEmail, MdLocationOn } from "react-icons/md";
import { Link } from "react-router-dom";
import logo from '../assets/logoo.png';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("Home");

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about" },
    { label: "Our Services", href: "#services", hasDropdown: true },
    { label: "Projects", href: "/projects" },
    { label: "Shop", href: "/products" },
    { label: "Contact", href: "/contact" },
  ];

  const services = [
    "Construction & Civil Works",
    "Fire Safety Solutions",
    "ICT & Security Solutions",
    "Pest & Fumigation",
  ];

  const serviceLinks = {
    "Construction & Civil Works": "/services/build-construction",
    "Fire Safety Solutions": "/services/fire-services",
    "ICT & Security Solutions": "/services/ict-and-security",
    "Pest & Fumigation": "/services/fumigation-services",
  };

  return (
    <header className="w-full bg-white shadow-sm">
      {/* Top row */}
      <div className="flex mx-auto max-w-6xl items-center justify-between px-6 py-3 border-b">
        <a href="/" className="flex items-center gap-3 mx-auto md:mx-0">
          <img
            src={logo}
            alt="Jighuluke logo"
            className="h-20 w-28"
          />
        </a>

        <div className="hidden md:flex items-center gap-8 text-gray-600">
          <div className="flex items-center gap-2">
            <MdLocationOn className="text-primary text-xl" />
            <div className="flex flex-col leading-tight">
              <span className="text-xs uppercase text-gray-500">Our Location</span>
              <span className="text-sm font-medium">Dar es Salaam, TZ</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <MdEmail className="text-primary text-xl" />
            <div className="flex flex-col leading-tight">
              <span className="text-xs uppercase text-gray-500">Email Us</span>
              <span className="text-sm font-medium">info@jighuluke.co.tz</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="flex items-center mx-auto max-w-6xl px-6 py-4 relative">
          {/* Desktop nav */}
          <div className="flex w-full justify-between items-center">
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 font-medium text-gray-700 hover:text-primary ${activeLink === link.label ? "border-b-2 border-primary" : ""
                        }`}
                    >
                      {link.label}
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" />
                      </svg>
                    </button>

                    {/* Desktop dropdown */}
                    <div
                      className={`absolute left-0 mt-3 w-[400px] shadow-lg bg-white border rounded-md transition-all duration-150 z-40 ${servicesOpen ? "opacity-100 visible" : "opacity-0 invisible"
                        }`}
                    >
                      <div className="p-4 flex flex-col gap-1">
                        {services.map((service) => (
                          <Link
                            key={service}
                            to={serviceLinks[service]}
                            onClick={() => {
                              setActiveLink(service);
                              setServicesOpen(false);
                            }}
                            className="block py-2 pl-6 text-gray-700 hover:text-primary font-medium rounded hover:bg-gray-50 transition"
                          >
                            {service}
                          </Link>

                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setActiveLink(link.label)}
                    className={`py-2 font-medium text-gray-700 hover:text-primary ${activeLink === link.label ? "border-b-2 border-primary" : ""
                      }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            <Link to="/quote">
              <button className="bg-primary text-secondary px-5 py-2 rounded-md hover:bg-primary/60 transition">
                Request Quote
              </button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden absolute right-6">
            <button
              className="p-2 rounded-md text-gray-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    mobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden transition-max-h duration-300 overflow-hidden ${mobileMenuOpen ? "max-h-screen" : "max-h-0"
          }`}
      >
        <nav className="px-4 py-4 flex flex-col gap-3 bg-white border-t">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.label} className="space-y-2">
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center gap-1 font-medium"
                >
                  {link.label}
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""
                      }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" />
                  </svg>
                </button>
                <div className={`${servicesOpen ? "block" : "hidden"} flex flex-col pl-6`}>
                  {services.map((service) => (
                    <Link
                      key={service}
                      to={serviceLinks[service]}
                      onClick={() => {
                        setActiveLink(service);
                        setMobileMenuOpen(false);
                      }}
                      className="py-2 text-gray-700 hover:text-primary font-medium rounded hover:bg-gray-50 transition"
                    >
                      {service}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => {
                  setActiveLink(link.label);
                  setMobileMenuOpen(false);
                }}
                className={`py-2 font-medium text-gray-700 hover:text-primary ${activeLink === link.label ? "border-b-2 border-primary" : ""
                  }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>
      </div>
    </header>
  );
}
